import { COURSE_CONFIG } from '../types';

/** Composite lookup key: `courseName|language` (used in fee override maps). */
export function courseLanguageFeeKey(courseName: string, language: string): string {
  return `${String(courseName || '').trim()}|${String(language || '').trim()}`;
}

function findOverrideFee(
  courseName: string,
  dbOverrides: Record<string, number> | null | undefined,
  language?: string | null
): number | undefined {
  if (!dbOverrides) return undefined;
  const lang = String(language || '').trim();
  if (lang) {
    const exactKey = courseLanguageFeeKey(courseName, lang);
    const fromExact = dbOverrides[exactKey];
    if (typeof fromExact === 'number' && !Number.isNaN(fromExact) && fromExact > 0) return fromExact;
    const coursePrefix = `${String(courseName || '').trim()}|`;
    for (const [key, val] of Object.entries(dbOverrides)) {
      if (!key.startsWith(coursePrefix)) continue;
      const keyLang = key.slice(coursePrefix.length);
      if (keyLang.toLowerCase() === lang.toLowerCase() && typeof val === 'number' && !Number.isNaN(val) && val > 0) {
        return val;
      }
    }
  }
  const fromDb = dbOverrides[courseName];
  if (typeof fromDb === 'number' && !Number.isNaN(fromDb) && fromDb > 0) return fromDb;
  return undefined;
}

/** Merge course base prices and per-language prices into one lookup map. */
export function buildCourseFeeOverrideMap(
  courses: Array<{ name: string; base_price?: number | null }>,
  languagePrices: Array<{ course_name: string; language: string; base_price: number }>
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const row of courses) {
    const name = String(row.name || '').trim();
    if (!name) continue;
    if (typeof row.base_price === 'number' && !Number.isNaN(row.base_price) && row.base_price > 0) {
      map[name] = row.base_price;
    }
  }
  for (const row of languagePrices) {
    const name = String(row.course_name || '').trim();
    const lang = String(row.language || '').trim();
    const price = Number(row.base_price);
    if (!name || !lang || !Number.isFinite(price) || price <= 0) continue;
    map[courseLanguageFeeKey(name, lang)] = price;
  }
  return map;
}

/** Delivery language on the course session — catalog language fees apply to this, not attendee/company preference. */
export function resolveCatalogFeeLanguage(sessionLanguage?: string | null): string | null {
  const lang = String(sessionLanguage || '').trim();
  return lang || null;
}

/** Catalog default fee for a session (course name + session delivery language). */
export function getCourseCatalogDefaultFeeForSession(
  courseName: string | undefined | null,
  dbOverrides?: Record<string, number> | null,
  sessionLanguage?: string | null
): number {
  return getCourseCatalogDefaultFee(
    courseName,
    dbOverrides,
    resolveCatalogFeeLanguage(sessionLanguage)
  );
}

/** True when the fee map has an explicit price for this course + language (not only the course base). */
export function hasLanguageSpecificCatalogFee(
  courseName: string | undefined | null,
  dbOverrides?: Record<string, number> | null,
  language?: string | null
): boolean {
  if (!courseName || !dbOverrides) return false;
  const lang = String(language || '').trim();
  if (!lang) return false;
  const exact = findOverrideFee(courseName, dbOverrides, lang);
  const base = findOverrideFee(courseName, dbOverrides, null);
  if (exact === undefined) return false;
  if (base === undefined) return true;
  return Math.abs(exact - base) > 0.005;
}

/**
 * Default session/course fee:
 * 1) course + language price from DB
 * 2) course base_price from DB
 * 3) optional defaultPrice in COURSE_CONFIG
 */
export function getCourseCatalogDefaultFee(
  courseName: string | undefined | null,
  dbOverrides?: Record<string, number> | null,
  language?: string | null
): number {
  if (!courseName) return 0;
  const fromOverrides = findOverrideFee(courseName, dbOverrides, language);
  if (fromOverrides !== undefined) return fromOverrides;
  const cfg = COURSE_CONFIG[courseName as keyof typeof COURSE_CONFIG];
  if (!cfg) return 0;
  const p = (cfg as { defaultPrice?: number }).defaultPrice;
  return typeof p === 'number' && !Number.isNaN(p) && p > 0 ? p : 0;
}

/** Discount % when session fee is below catalog default; null if no meaningful discount. */
export function getFeeDiscountPercentVsCatalog(
  courseName: string | undefined | null,
  sessionFee: number,
  dbOverrides?: Record<string, number> | null,
  language?: string | null
): number | null {
  const catalog = getCourseCatalogDefaultFee(courseName, dbOverrides, language);
  if (catalog <= 0 || sessionFee >= catalog - 0.005) return null;
  const pct = Math.round((1 - sessionFee / catalog) * 100);
  return pct > 0 ? pct : null;
}
